import { type LoaderFunctionArgs } from '@remix-run/node';
import { Outlet, Link, useRouteLoaderData } from '@remix-run/react';

import { useState } from 'react';
import { useLocale } from 'remix-i18next/react';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';
import { MonochromeCrispyVoiceLogo } from '#common/components/MonochromeCrispyVoiceLogo';
import { prisma } from '#common/db.server';
import { useUrlWithQueryParams } from '#common/hooks/useUrlWithQueryParams.ts';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { useTranslatedPath } from '#common/utils/i18n.js';
import { LinkTypeIcon, type LinkType } from '#common/utils/link-types.js';
import { Button } from '#speaker-shop/components/Button.tsx';
import { ContactFormDialog } from '#speaker-shop/components/ContactFormDialog.js';
import { CoreLegalPageLayout } from '#speaker-shop/components/CoreLegalPageLayout.tsx';
import { getVoicePageData } from '#speaker-shop/models/voice-page-data.server.ts';
import { getSpeakerShopData } from '#speaker-shop/models/speaker-shop-data.server.js';
import { getLocale } from '#speaker-shop/i18n/i18n.server';
import { getUser } from '#speaker-shop/session.server';
import { resolveVoiceAndLangParams } from '#speaker-shop/utils/resolve-voice';
import { getFirstVoiceSlug, resolveShopContext } from '#speaker-shop/utils/shop-context.server.ts';

export async function loader({ request, params }: LoaderFunctionArgs) {
    const { voiceSlug, lang } = resolveVoiceAndLangParams(params);
    const shopContext = await resolveShopContext(request.url, voiceSlug);
    const segments = new URL(request.url).pathname.split('/').filter(Boolean);
    if (shopContext.unifiedShopPage && shopContext.speakerId) {
        const speaker = await prisma.speaker.findUniqueOrThrow({
            where: { id: shopContext.speakerId },
            select: { shopLinks: true }
        });
        const links = speaker.shopLinks.map(link => ({ type: link.type, url: link.url }));
        if (!voiceSlug || (segments.length === (lang ? 2 : 1) && segments.includes(voiceSlug))) {
            return typedjson({ links, unifiedShopPage: true as const });
        }
        const locale = await getLocale(await getUser(request), lang, request);
        return typedjson({ links, ...await getVoicePageData(request, voiceSlug, locale, shopContext) });
    }

    const isLandingIndex = shopContext.shopMode === 'SINGLE_SUBDOMAIN' && !voiceSlug
        && (segments.length === 0 || (segments.length === 1 && segments[0] === lang));
    if (isLandingIndex) {
        return typedjson({ links: [] as { type: string; url: string }[] });
    }
    const effectiveVoiceSlug = voiceSlug
        ?? (shopContext.shopMode === 'SINGLE_SUBDOMAIN' && shopContext.speakerId
            ? await getFirstVoiceSlug(shopContext.speakerId, shopContext.isPreview) ?? undefined
            : undefined);
    const speakerShopData = await getSpeakerShopData(request.url, effectiveVoiceSlug);
    if (!speakerShopData) {
        throw new Response('No speaker shop data found', { status: 404 });
    }

    const links = speakerShopData.speakerVoice.speaker.unifiedShopPage
        ? speakerShopData.speakerVoice.speaker.shopLinks
        : speakerShopData.links;
    const locale = await getLocale(await getUser(request), lang, request);
    return typedjson({
        links: links.map(x => ({ type: x.type, url: x.url })),
        ...await getVoicePageData(request, voiceSlug, locale, shopContext)
    });
}

export default function Layout() {
    const layoutData = useTypedLoaderData<typeof loader>();
    const { links } = layoutData;
    const indexData = useRouteLoaderData<{ unified?: unknown; voices?: unknown }>(
        'routes/_index+/($voice).($lang)+/index'
    );
    if (indexData && ('unified' in indexData || 'voices' in indexData)) {
        return <Outlet />;
    }
    if ('unifiedShopPage' in layoutData && layoutData.unifiedShopPage) {
        return (
            <CoreLegalPageLayout links={links}>
                <Outlet />
            </CoreLegalPageLayout>
        );
    }
    return (
        <div className='min-h-screen flex flex-col'>
            <div className='grow'>
                <Outlet />
            </div>
            <Footer />
            {/* <TailwindBreakpointIndicator /> */}
        </div>
    );
}

function Footer() {
    const locale = useLocale();
    const { t } = useEditableTranslation('footer');
    const [ showContactForm, setShowContactForm ] = useState(false);
    const translatePath = useTranslatedPath(locale);
    return (
        <>
            <div className='bg-radial from-crispybluedark-600 to-crispybluedark-700 w-full flex flex-col items-center pt-20 pb-8 gap-y-6 px-4 text-white'>
                <MonochromeCrispyVoiceLogo className='h-20 mb-4' />
                <SocialLinks />
                <div className='flex flex-col items-center'>
                    <Link to={useUrlWithQueryParams(translatePath('/gtc'), [ 'variant' ])}>{t('gtc')}</Link>
                    <Link to={useUrlWithQueryParams(translatePath('/imprint'), [ 'variant' ])}>{t('imprint')}</Link>
                </div>
                <Button color='white' size='big' className='mt-4' onClick={() => setShowContactForm(true)}>
                    {t('contact-real-voice')}
                </Button>
                <p>{t('copyright', { year: new Date().getFullYear() })}</p>
            </div>
            <ContactFormDialog
                topic={'Contact real voice'}
                description={
                    <div>
                        <p>{t('common:contact-form-description')}</p>
                        <p>{t('common:contact-form-instruction')}</p>
                    </div>
                }
                open={showContactForm}
                onClose={() => setShowContactForm(false)}
            />
        </>
    );
}

function SocialLinks() {
    const { links } = useTypedLoaderData<typeof loader>();
    return (
        <div className='flex flex-row gap-x-3'>
            {links.map(x => (
                <Link key={x.url} to={x.url} target='_blank'>
                    <LinkTypeIcon type={x.type as LinkType} className='h-8 w-8' />
                </Link>
            ))}
        </div>
    );
}
